import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import { 
  getDimensions, 
  getTagsByDimension, 
  createTag, 
  updateTag, 
  deleteTag,
  getTagFeatureCounts 
} from '../services/featureService';
import TagView from './TagView';
import { notifyDimensionOrTagListChanged } from '../utils/dimensionTagSync';
import './Tags.css';

const DIMENSION_TYPE_LABELS = {
  'BOOLEAN': 'Boolean',
  'TRI_STATE': 'Yes-No',
  'RATING': 'Rating'
};

const Tags = ({ featureGroupId, userRole }) => {
  const [dimensions, setDimensions] = useState([]);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [tags, setTags] = useState([]);
  const [totalTagCount, setTotalTagCount] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [viewingTag, setViewingTag] = useState(null);
  const [featureCounts, setFeatureCounts] = useState(new Map());
  const [expandedDimensions, setExpandedDimensions] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    keywords: []
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [errors, setErrors] = useState({});

  const isOwner = userRole === 'Owner';
  const canEditTags = (dimension) => {
    if (isOwner) return true;
    if (userRole === 'Editor') return dimension?.editable_to_editor ?? true;
    if (userRole === 'Guest') return dimension?.editable_to_guest ?? false;
    return false;
  };

  const loadDimensions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const allDimensions = await getDimensions(featureGroupId);
      
      // Filter dimensions by visibility - show only dimensions visible to editors/guests
      // Even if user is owner, only show dimensions visible to non-owners
      const visibleDimensions = allDimensions.filter(dim => {
        if (userRole === 'Editor') return dim.visible_to_editor;
        if (userRole === 'Guest') return dim.visible_to_guest;
        // For owners, show dimensions visible to editors or guests
        return dim.visible_to_editor || dim.visible_to_guest;
      });
      
      setDimensions(visibleDimensions);
      
      // Auto-select first dimension if available
      if (visibleDimensions.length > 0 && !selectedDimension) {
        setSelectedDimension(visibleDimensions[0]);
        setExpandedDimensions(new Set([visibleDimensions[0].id]));
      }
    } catch (err) {
      setError('Failed to load dimensions');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [featureGroupId, userRole, selectedDimension]);

  const loadTagsForDimension = useCallback(async (dimensionId) => {
    try {
      setLoading(true);
      // First load all tags (without filter) to get total count
      const allTags = await getTagsByDimension(dimensionId, '');
      setTotalTagCount(allTags?.length || 0);
      
      // Then load filtered tags
      const data = await getTagsByDimension(dimensionId, filter);
      setTags(data || []);
      
      // Load feature counts for all tags
      if (data && data.length > 0) {
        const tagIds = data.map(tag => tag.id);
        const counts = await getTagFeatureCounts(tagIds);
        setFeatureCounts(counts);
      } else {
        setFeatureCounts(new Map());
      }
    } catch (err) {
      setError('Failed to load tags');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (featureGroupId) {
      loadDimensions();
    }
  }, [featureGroupId, userRole, loadDimensions]);

  useEffect(() => {
    if (selectedDimension) {
      loadTagsForDimension(selectedDimension.id);
    }
  }, [selectedDimension, filter, loadTagsForDimension]);

  const handleDimensionClick = (dimension) => {
    setSelectedDimension(dimension);
    // Only allow one dimension to be expanded at a time
    // If clicking the same dimension, toggle it; otherwise, expand the new one and collapse others
    const isCurrentlyExpanded = expandedDimensions.has(dimension.id);
    if (isCurrentlyExpanded && selectedDimension?.id === dimension.id) {
      // Toggle: collapse if clicking the same expanded dimension
      setExpandedDimensions(new Set());
    } else {
      // Expand the clicked dimension and collapse all others
      setExpandedDimensions(new Set([dimension.id]));
    }
  };

  const handleAdd = (dimension = null) => {
    const targetDimension = dimension || selectedDimension;
    if (!targetDimension) {
      alert('Please select a dimension first');
      return;
    }
    setSelectedDimension(targetDimension);
    setEditingTag(null);
    setFormData({ name: '', keywords: [] });
    setKeywordInput('');
    setErrors({});
    setShowAddForm(true);
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      keywords: tag.keywords || []
    });
    setKeywordInput('');
    setErrors({});
    setShowAddForm(true);
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Are you sure you want to delete the tag "${tag.name}"?`)) {
      return;
    }

    try {
      await deleteTag(tag.id);
      notifyDimensionOrTagListChanged(featureGroupId);
      if (selectedDimension) {
        await loadTagsForDimension(selectedDimension.id);
      }
    } catch (err) {
      alert('Failed to delete tag');
      logger.error(err);
    }
  };

  const addKeyword = () => {
    const keyword = keywordInput.trim();
    if (keyword && !formData.keywords.includes(keyword)) {
      setFormData({
        ...formData,
        keywords: [...formData.keywords, keyword]
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter(k => k !== keyword)
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Tag name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!selectedDimension) {
      alert('Please select a dimension first');
      return;
    }

    try {
      if (editingTag) {
        await updateTag(editingTag.id, formData);
      } else {
        await createTag({ 
          dimension_id: selectedDimension.id,
          name: formData.name.trim(),
          keywords: formData.keywords || []
        });
      }
      setShowAddForm(false);
      setEditingTag(null);
      setFormData({ name: '', keywords: [] });
      setKeywordInput('');
      notifyDimensionOrTagListChanged(featureGroupId);
      await loadTagsForDimension(selectedDimension.id);
    } catch (err) {
      alert(editingTag ? 'Failed to update tag' : 'Failed to create tag');
      logger.error(err);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingTag(null);
    setFormData({ name: '', keywords: [] });
    setKeywordInput('');
    setErrors({});
  };

  if (viewingTag) {
    return (
      <TagView
        tag={viewingTag}
        featureGroupId={featureGroupId}
        onClose={() => setViewingTag(null)}
      />
    );
  }

  return (
    <div className="tags-container">
      <div className="tags-header">
        <h2>Tags</h2>
      </div>

      {loading && <div className="loading">Loading dimensions...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="dimensions-list-view">
            <h3>Dimensions</h3>
            {dimensions.length === 0 ? (
              <div className="no-dimensions">
                No dimensions available. Dimensions must be visible to {userRole === 'Editor' ? 'Editors' : 'Guests'}.
              </div>
            ) : (
              <div className="dimensions-list">
                {dimensions.map(dimension => {
                  const isExpanded = expandedDimensions.has(dimension.id);
                  const isSelected = selectedDimension?.id === dimension.id;
                  
                  return (
                    <div key={dimension.id} className={`dimension-item ${isSelected ? 'selected' : ''}`}>
                      <div 
                        className="dimension-header"
                        onClick={() => handleDimensionClick(dimension)}
                      >
                        <span className="dimension-toggle">
                          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                        </span>
                        <span className="dimension-name">{dimension.name}</span>
                        {isExpanded && canEditTags(dimension) && !showAddForm && (
                          <button 
                            className="add-tag-button-inline" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAdd(dimension);
                            }}
                            title="Add tag to this dimension"
                          >
                            <i className="fas fa-plus"></i> Add Tag
                          </button>
                        )}
                        <span className={`dimension-type-badge type-${dimension.dimension_type.toLowerCase()}`}>
                          {DIMENSION_TYPE_LABELS[dimension.dimension_type] || dimension.dimension_type}
                        </span>
                      </div>
                      
                      {isExpanded && isSelected && (
                        <div className="dimension-tags-section">
                          {totalTagCount > 7 && (
                            <div className="tags-filter">
                              <input
                                type="text"
                                placeholder="Filter tags by name or keyword..."
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="filter-input"
                              />
                            </div>
                          )}

                          {showAddForm && canEditTags(selectedDimension) && (
                            <div className="tag-form-container">
                              <form onSubmit={handleSubmit} className="tag-form">
                                <h4>{editingTag ? 'Edit Tag' : 'Add New Tag'}</h4>
                                
                                <div className="form-group">
                                  <label>
                                    Tag Name <span className="required">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className={errors.name ? 'error' : ''}
                                    placeholder="Enter tag name"
                                  />
                                  {errors.name && <span className="error-message">{errors.name}</span>}
                                </div>

                                <div className="form-group">
                                  <label>Keywords</label>
                                  <div className="keywords-input">
                                    <input
                                      type="text"
                                      value={keywordInput}
                                      onChange={(e) => setKeywordInput(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          addKeyword();
                                        }
                                      }}
                                      placeholder="Enter keyword and press Enter"
                                    />
                                    <button type="button" onClick={addKeyword} className="add-keyword-button">
                                      <i className="fas fa-plus"></i>
                                    </button>
                                  </div>
                                  {formData.keywords.length > 0 && (
                                    <div className="keywords-list">
                                      {formData.keywords.map((keyword, index) => (
                                        <span key={index} className="keyword-tag">
                                          {keyword}
                                          <button
                                            type="button"
                                            onClick={() => removeKeyword(keyword)}
                                            className="remove-keyword"
                                          >
                                            <i className="fas fa-times"></i>
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                <div className="form-actions">
                                  <button type="button" onClick={handleCancel} className="cancel-button">
                                    Cancel
                                  </button>
                                  <button type="submit" className="save-button">
                                    {editingTag ? 'Update' : 'Create'} Tag
                                  </button>
                                </div>
                              </form>
                            </div>
                          )}

                          {tags.length === 0 ? (
                            <div className="no-tags">
                              {filter ? 'No tags match your filter.' : 'No tags yet.'}
                            </div>
                          ) : (
                            <table className="tags-table">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Keywords</th>
                                  <th>Feature Count</th>
                                  {canEditTags(selectedDimension) && <th>Actions</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {tags.map(tag => (
                                  <tr key={tag.id}>
                                    <td className="tag-name" title={tag.id}>{tag.name}</td>
                                    <td>
                                      {tag.keywords && tag.keywords.length > 0 ? (
                                        <div className="keywords-display">
                                          {tag.keywords.slice(0, 3).map((keyword, index) => (
                                            <span key={index} className="keyword-chip">{keyword}</span>
                                          ))}
                                          {tag.keywords.length > 3 && (
                                            <span className="keyword-more">+{tag.keywords.length - 3} more</span>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="no-keywords">None</span>
                                      )}
                                    </td>
                                    <td className="feature-count">
                                      {featureCounts.get(tag.id) || 0}
                                    </td>
                                    {canEditTags(selectedDimension) && (
                                      <td className="tag-actions">
                                        <button
                                          className="action-button"
                                          onClick={() => handleEdit(tag)}
                                          title="Edit tag"
                                        >
                                          <i className="fas fa-edit"></i>
                                        </button>
                                        <button
                                          className="action-button delete"
                                          onClick={() => handleDelete(tag)}
                                          title="Delete tag"
                                        >
                                          <i className="fas fa-trash"></i>
                                        </button>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Tags;

